import React from 'react';
import { 
  Alert, 
  AlertTitle, 
  Box, 
  Button, 
  Typography,
  Collapse
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ErrorContainer = styled(Box)(({ theme, fullPage }) => ({
  margin: theme.spacing(2, 0),
  ...(fullPage && {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    padding: theme.spacing(3),
  }),
}));

const StyledAlert = styled(Alert)(({ theme, severity }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  '& .MuiAlert-icon': {
    fontSize: '1.5rem',
  },
}));

const ErrorDetails = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  maxHeight: '150px',
  overflow: 'auto',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

/**
 * Error message component
 * @param {Object} props Component props
 * @param {string} props.title Error title
 * @param {string} props.message Error message
 * @param {string} props.details Technical error details (optional)
 * @param {Function} props.onRetry Retry function (optional)
 * @param {boolean} props.fullPage Whether to display as a full-page error
 * @returns {JSX.Element} ErrorMessage component
 */
const ErrorMessage = ({ 
  title = 'Error', 
  message = 'An unexpected error occurred', 
  details,
  onRetry,
  fullPage = false,
  severity = 'error'
}) => {
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <ErrorContainer fullPage={fullPage}>
      {fullPage && (
        <ErrorOutlineIcon 
          color="error" 
          sx={{ fontSize: 64, mb: 2 }} 
        />
      )}
      
      <StyledAlert 
        severity={severity} 
        variant="filled"
        action={
          details && (
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>
          )
        }
      >
        <AlertTitle>{title}</AlertTitle>
        {message}
        
        <Collapse in={showDetails}>
          {details && (
            <ErrorDetails component="pre">
              {details}
            </ErrorDetails>
          )}
        </Collapse>
      </StyledAlert>
      
      {onRetry && (
        <ActionButton 
          variant="contained" 
          color="primary" 
          onClick={onRetry}
        >
          Retry
        </ActionButton>
      )}
    </ErrorContainer>
  );
};

export default ErrorMessage;
